// A18: integrations panel — surfaces the per-workspace CI/CD webhook
// URL + signing secret. Captain pastes the URL into their CI provider's
// post-deploy hook (Jenkins, internal scripts, anything that doesn't
// post to GitHub directly). Most modern providers are covered by the
// orchestrator's GitHub Checks/Statuses polling automatically — this
// panel exists for the catch-all webhook path.

import { useEffect, useState } from "react";
import {
  ApiError,
  getCicdWebhookUrl,
  revokeCicdWebhook,
  rotateCicdWebhook,
} from "../api/client";

interface Props {
  workspaceId: number;
}

export function IntegrationsPanel({ workspaceId }: Props) {
  const [url, setUrl] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState<"load" | "rotate" | "revoke" | null>("load");
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const refresh = async () => {
    setError(null);
    setBusy("load");
    try {
      const resp = await getCicdWebhookUrl(workspaceId);
      setUrl(resp.url);
      setConfigured(resp.configured);
      setSecret("");
      setShowSecret(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const rotate = async () => {
    setBusy("rotate");
    setError(null);
    try {
      const resp = await rotateCicdWebhook(workspaceId);
      setUrl(resp.url);
      setSecret(resp.secret);
      setConfigured(true);
      setShowSecret(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    setBusy("revoke");
    setError(null);
    try {
      await revokeCicdWebhook(workspaceId);
      setConfigured(false);
      setSecret("");
      setShowSecret(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="integrations-panel"
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">Integrations</h3>
        <p className="mt-1 text-xs text-muted">
          Most CI/CD providers (Vercel, Netlify, Koyeb, Fly, Railway,
          Render, CircleCI, GitHub Actions) post results back to GitHub
          on the commits Waytide pushes — Waytide reads those without
          extra setup. This webhook is for tools that don't talk to
          GitHub: paste the URL into the provider's post-deploy hook,
          paste the secret into the same hook's signing-key slot.
        </p>
      </header>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-primary">Webhook URL</label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={url}
            readOnly
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 font-code text-xs text-primary"
            data-testid="integrations-webhook-url"
          />
          <button
            type="button"
            onClick={() => void navigator.clipboard.writeText(url)}
            disabled={!url}
            className="rounded-md border border-edge px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
          >
            Copy
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-primary">
          HMAC secret
          <span className="ml-2 text-[0.625rem] text-faint">
            (shown once on rotate; sign request body with HMAC-SHA256
            and send as `X-Waytide-Signature: hex`)
          </span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={
              showSecret
                ? secret
                : configured
                  ? "•••••••••••••••• (rotate to reveal)"
                  : "(no secret configured)"
            }
            readOnly
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 font-code text-xs text-primary"
            data-testid="integrations-webhook-secret"
          />
          <button
            type="button"
            onClick={() => void navigator.clipboard.writeText(secret)}
            disabled={!secret}
            className="rounded-md border border-edge px-2 py-1 text-[0.625rem] font-medium text-muted hover:border-accent hover:text-accent disabled:opacity-50"
          >
            Copy
          </button>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {configured && (
          <button
            type="button"
            onClick={() => void revoke()}
            disabled={busy !== null}
            className="rounded-md border border-edge px-2 py-1 text-[0.625rem] text-muted hover:border-danger hover:text-danger disabled:opacity-50"
          >
            {busy === "revoke" ? "Revoking…" : "Revoke"}
          </button>
        )}
        <button
          type="button"
          onClick={() => void rotate()}
          disabled={busy !== null}
          className="rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
        >
          {busy === "rotate"
            ? "Rotating…"
            : configured
              ? "Rotate secret"
              : "Generate"}
        </button>
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </section>
  );
}
